import { SxProps } from "@mui/material/styles";

export const richTextStyles: SxProps = {
	// Headings — match text editor .tiptap sizing
	"& h1": {
		fontSize: "2rem",
		fontWeight: 600,
		lineHeight: 1.2,
		mb: 2,
		mt: 0,
		color: "var(--mui-palette-text-primary)",
	},
	"& h2": {
		fontSize: "1.75rem",
		fontWeight: 600,
		lineHeight: 1.3,
		mb: 1.5,
		mt: 2,
		color: "var(--mui-palette-text-primary)",
	},
	"& h3": {
		fontSize: "1.5rem",
		fontWeight: 600,
		lineHeight: 1.4,
		mb: 1.5,
		mt: 2,
		color: "var(--mui-palette-text-primary)",
	},
	"& h4": {
		fontSize: "1.25rem",
		fontWeight: 600,
		lineHeight: 1.4,
		mb: 1,
		mt: 2,
		color: "var(--mui-palette-text-primary)",
	},
	"& h5": {
		fontSize: "1.125rem",
		fontWeight: 600,
		lineHeight: 1.4,
		mb: 1,
		mt: 2,
		color: "var(--mui-palette-text-primary)",
	},
	"& h6": {
		fontSize: "1rem",
		fontWeight: 600,
		lineHeight: 1.4,
		mb: 1,
		mt: 2,
		color: "var(--mui-palette-text-primary)",
	},
	"& p": {
		fontSize: "1em",
		lineHeight: 1.6,
		mb: 1,
		color: "var(--mui-palette-text-primary)",
	},
	"& strong": { fontWeight: 600 },
	"& em": { fontStyle: "italic" },
	"& code": {
		backgroundColor: "var(--mui-palette-neutral-100)",
		borderRadius: "4px",
		padding: "2px 6px",
		fontSize: "0.8125rem",
		fontFamily: "monospace",
	},
	"& pre": {
		backgroundColor: "var(--mui-palette-neutral-100)",
		borderRadius: "6px",
		padding: 2,
		mb: 2,
		overflow: "auto",
		"& code": {
			backgroundColor: "transparent",
			padding: 0,
		},
	},
	"& blockquote": {
		borderLeft: "4px solid var(--mui-palette-primary-main)",
		backgroundColor: "var(--mui-palette-neutral-50)",
		padding: 2,
		margin: "16px 0",
		fontStyle: "italic",
		"& p": { mb: 0 },
	},
	"& ul, & ol": { pl: 3, mb: 2 },
	"& li": {
		mb: 0.5,
		fontSize: "1em",
		lineHeight: 1.6,
		color: "var(--mui-palette-text-primary)",
	},
	"& hr": {
		border: 0,
		borderTop: "1px solid var(--mui-palette-divider)",
		my: 2,
	},
	"& a": {
		color: "var(--mui-palette-primary-main)",
		textDecoration: "none",
		"&:hover": {
			textDecoration: "underline",
		},
	},
	"& img": {
		display: "block",
		maxWidth: "100%",
		height: "auto",
		borderRadius: "6px",
		mb: 2,
	},
	"& iframe": {
		maxWidth: "100%",
		width: "100%",
		borderRadius: "6px",
		mb: 2,
		border: "none",
	},

	// Tables — match text editor's .tiptap table rendering
	"& table": {
		width: "100%",
		borderCollapse: "collapse",
		tableLayout: "fixed",
		mb: 2,
		overflow: "hidden",
	},
	"& table td, & table th": {
		minWidth: "1em",
		border: "2px solid var(--mui-palette-divider)",
		padding: "0",
		verticalAlign: "top",
		boxSizing: "border-box",
		position: "relative",
	},
	"& table th": {
		fontWeight: "bold",
		textAlign: "left",
		backgroundColor: "var(--mui-palette-grey-50)",
	},
	"& table[data-invisible-borders='true'] td, & table[data-invisible-borders='true'] th":
		{
			border: "none",
		},
	"& table p": {
		margin: 0,
		padding: "3px 5px",
	},
	"& table h1, & table h2, & table h3, & table h4, & table h5, & table h6": {
		margin: 0,
		padding: "3px 5px",
	},
	"& table ul, & table ol": {
		margin: 0,
		padding: "3px 5px 3px 25px",
	},
	"& table blockquote": {
		margin: 0,
		padding: "3px 5px",
	},

	// Variables & equations — highlight as editor placeholders
	"& [data-type='variable-placeholder'], & [data-type='equation']": {
		backgroundColor: "var(--mui-palette-primary-50)",
		padding: "0 4px",
		borderRadius: "4px",
		fontSize: "0.85em",
	},

	// Charts — responsive overrides (popup processor may set inline dimensions)
	"& [data-type='chart']": {
		maxWidth: "100%",
		width: "100%",
		minWidth: 0,
		overflow: "hidden",
		"& .recharts-responsive-container": {
			maxWidth: "100%",
			width: "100%",
			minWidth: 0,
		},
		"& > div": {
			maxWidth: "100%",
			width: "100%",
			minWidth: 0,
		},
		"& svg": {
			maxWidth: "100%",
			height: "auto",
		},
	},

	// Interactive buttons — match editor's data-color/data-size/data-variant attributes
	"& [data-type='interactive-button']": {
		display: "inline-block",
		backgroundColor: "var(--mui-palette-primary-main)",
		color: "var(--mui-palette-primary-contrastText)",
		padding: "8px 16px",
		borderRadius: "6px",
		textDecoration: "none",
		fontSize: "0.875rem",
		fontWeight: 500,
		margin: "4px 8px 4px 0",
		cursor: "pointer",
		"&:hover": {
			backgroundColor: "var(--mui-palette-primary-dark)",
		},
		// Color variants
		"&[data-color='secondary']": {
			backgroundColor: "var(--mui-palette-secondary-main)",
			color: "var(--mui-palette-secondary-contrastText)",
			"&:hover": {
				backgroundColor: "var(--mui-palette-secondary-dark)",
			},
		},
		"&[data-color='error']": {
			backgroundColor: "var(--mui-palette-error-main)",
			color: "var(--mui-palette-error-contrastText)",
			"&:hover": {
				backgroundColor: "var(--mui-palette-error-dark)",
			},
		},
		"&[data-color='success']": {
			backgroundColor: "var(--mui-palette-success-main)",
			color: "var(--mui-palette-success-contrastText)",
			"&:hover": {
				backgroundColor: "var(--mui-palette-success-dark)",
			},
		},
		"&[data-color='info']": {
			backgroundColor: "var(--mui-palette-info-main)",
			color: "var(--mui-palette-info-contrastText)",
			"&:hover": {
				backgroundColor: "var(--mui-palette-info-dark)",
			},
		},
		"&[data-color='warning']": {
			backgroundColor: "var(--mui-palette-warning-main)",
			color: "var(--mui-palette-warning-contrastText)",
			"&:hover": {
				backgroundColor: "var(--mui-palette-warning-dark)",
			},
		},
		// Size variants
		"&[data-size='small']": {
			padding: "6px 12px",
			fontSize: "0.8125rem",
		},
		"&[data-size='large']": {
			padding: "12px 24px",
			fontSize: "1rem",
		},
		// Variant: outlined
		"&[data-variant='outlined']": {
			backgroundColor: "transparent",
			color: "var(--mui-palette-primary-main)",
			border: "1px solid var(--mui-palette-primary-main)",
			"&:hover": {
				backgroundColor: "var(--mui-palette-primary-50)",
			},
			"&[data-color='secondary']": {
				color: "var(--mui-palette-secondary-main)",
				borderColor: "var(--mui-palette-secondary-main)",
				"&:hover": {
					backgroundColor: "var(--mui-palette-secondary-50)",
				},
			},
			"&[data-color='error']": {
				color: "var(--mui-palette-error-main)",
				borderColor: "var(--mui-palette-error-main)",
				"&:hover": {
					backgroundColor: "var(--mui-palette-error-50)",
				},
			},
			"&[data-color='success']": {
				color: "var(--mui-palette-success-main)",
				borderColor: "var(--mui-palette-success-main)",
				"&:hover": {
					backgroundColor: "var(--mui-palette-success-50)",
				},
			},
			"&[data-color='info']": {
				color: "var(--mui-palette-info-main)",
				borderColor: "var(--mui-palette-info-main)",
				"&:hover": {
					backgroundColor: "var(--mui-palette-info-50)",
				},
			},
			"&[data-color='warning']": {
				color: "var(--mui-palette-warning-main)",
				borderColor: "var(--mui-palette-warning-main)",
				"&:hover": {
					backgroundColor: "var(--mui-palette-warning-50)",
				},
			},
		},
		// Variant: text
		"&[data-variant='text']": {
			backgroundColor: "transparent",
			color: "var(--mui-palette-primary-main)",
			"&:hover": {
				backgroundColor: "var(--mui-palette-primary-50)",
			},
			"&[data-color='secondary']": {
				color: "var(--mui-palette-secondary-main)",
				"&:hover": {
					backgroundColor: "var(--mui-palette-secondary-50)",
				},
			},
			"&[data-color='error']": {
				color: "var(--mui-palette-error-main)",
				"&:hover": {
					backgroundColor: "var(--mui-palette-error-50)",
				},
			},
			"&[data-color='success']": {
				color: "var(--mui-palette-success-main)",
				"&:hover": {
					backgroundColor: "var(--mui-palette-success-50)",
				},
			},
			"&[data-color='info']": {
				color: "var(--mui-palette-info-main)",
				"&:hover": {
					backgroundColor: "var(--mui-palette-info-50)",
				},
			},
			"&[data-color='warning']": {
				color: "var(--mui-palette-warning-main)",
				"&:hover": {
					backgroundColor: "var(--mui-palette-warning-50)",
				},
			},
		},
	},
};
